import React, { SetStateAction, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled, { css, DefaultTheme, keyframes, withTheme } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import FocusLock from 'react-focus-lock';
import MagicLineIcon from 'remixicon-react/MagicLineIcon';
import Button from '../../atoms/Button/Button';
import Spinner from '../../atoms/Spinner/Spinner';
import AiToggle from '../../atoms/Toggle/AiToggle';
import { searchValidationConfig } from '../../../types/ValidationTypes';

import searchActions from '../../../store/search/actions';

import { useAppState } from '../../../store/appstate';
import Term from '../../atoms/Term/Term';
import {
    createEncodedSearchQueryString,
    generateQuoteSearch,
    parseQuery,
    parseSearchUrl,
} from '../../../services/helpers/queryStringHelpers';
import { parseFilters } from '../../../services/helpers/filterHelpers';

import CheckBox from '../../atoms/Checkbox/Checkbox';
import { ConnectedStore } from '../../../types/ConnectedStore';
import RelativeOperationTypes from '../../../types/RelativeOperation';

import DateRange from '../../atoms/DateRange/DateRange';
import {
    saveExactSearch,
    setAiSearchEnabled,
    setPerPage,
    UserSettingsState,
} from '../../../store/userSettings/userSettingsSlice';
import {
    aiMessageChanged,
    aiPromptChanged,
    SearchState,
} from '../../../store/search/searchSlice';
import { useAppDispatch } from '../../../store';
import StoreTerm from '../../atoms/Term/StoreTerm';
import OrderTabType from '../../../types/order/OrderTabType';
import {
    getAccountSearchFilters,
    getOrderSearchFilters,
} from '../../../services/helpers/search/orderAccountSearchHelper';
import Flex from '../../atoms/Box/Flex';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import OrderSearchTabType from '../../../types/order/OrderTabType';
import TermCategory from '../../../types/filter/TermCategory';
import { SessionState } from '../../../store/session/sessionSlice';
import { getStoresAvailableForUser } from '../../../services/helpers/storeHelper';
import { interpretOrderSearch } from '../../../services/aiOrderSearchService';
import ReadOnlyFilterTag, { RANGE_FILTER_KEYS } from '../../atoms/Term/ReadOnlyFilterTag';
import AiFeedback from '../../atoms/Message/AiFeedback';
import OrderStatus from '../../../types/order/OrderStatus';

const AI_DISCLAIMER = 'AI results may be inaccurate';
const ORDER_STATUSES = Object.values(OrderStatus).filter(Boolean);

const AI_ONLY_FILTER_KEYS = [
    'paymentMethod',
    'currency',
    'saleSegment',
    'countryCode',
    'productCode',
    'actionReference',
    'connectedAccounts.invoices.invoiceState',
    'connectedAccounts.invoices.paymentStatus.status',
    'connectedAccounts.invoices.isCanceled',
    'rows.status',
];

type Inputs = {
    searchPhrase: string;
    filter: string[];
};

export enum SearchTerms {
    PLACED_AT = 'placedAt',
    EXPIRES_AT = 'expiresAt',
    INVOICES_EXPIRES_AT = 'connectedAccounts.invoices.dueDate',
}

const today = new Date();

const fitToContent = (textArea: HTMLTextAreaElement | null | undefined) => {
    if (!textArea) return;

    textArea.style.height = 'auto';
    textArea.style.height = `${
        textArea.scrollHeight + textArea.offsetHeight - textArea.clientHeight
    }px`;
};

const relativeCreatedRanges = [
    { name: 'today', label: 'Today', value: '0d' },
    { name: 'yesterday', label: 'Yesterday', value: 'yesterday' },
    { name: 'last7days', label: 'Last 7 days', value: '-7d' },
    { name: 'last30days', label: 'Last 30 days', value: '-30d' },
    {
        name: 'custom',
        label: 'Custom',
        value: '-1d',
    },
];

const relativeExpiresRanges = [
    { name: 'today', label: 'Today', value: '0d' },
    { name: 'within7days', label: 'Within 7 days', value: '+7d' },
    { name: 'within30days', label: 'Within 30 days', value: '+30d' },
    {
        name: 'custom',
        label: 'Custom',
        value: '+1d',
    },
];

const dateRanges = [
    {
        maxDate: today,
        label: 'Created',
        SearchTerm: SearchTerms.PLACED_AT,
        ranges: relativeCreatedRanges,
        relativeLabel: { type: 'Last', operation: RelativeOperationTypes.SUBTRACT },
    },
    {
        label: 'Expires',
        SearchTerm: SearchTerms.EXPIRES_AT,
        ranges: relativeExpiresRanges,
        relativeLabel: { type: 'Within', operation: RelativeOperationTypes.ADD },
    },
    {
        label: 'Notification due dates',
        SearchTerm: SearchTerms.INVOICES_EXPIRES_AT,
    },
];

interface SearchFormProps {
    setResetTableSelects: React.Dispatch<SetStateAction<boolean>>;
    clearSearchRef?: React.MutableRefObject<(() => void) | undefined>;
    theme: DefaultTheme;
}

const SearchForm: React.FC<SearchFormProps> = ({
    setResetTableSelects,
    clearSearchRef,
    theme,
}: SearchFormProps) => {
    const SEARCH_PHRASE = 'searchPhrase';
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues,
    } = useForm<Inputs>();
    const { user } = useAppState<SessionState>(s => s.session);
    const {
        terms,
        isSearching,
        searchCancelToken,
        isSearchingAccounts,
        accountSearchCancelToken,
        aiPrompt = '',
        aiMessage = '',
    } = useAppState<SearchState>(s => s.search);
    const {
        orderColumnFilters: sessionColumns,
        exactSearch: sessionExactSearch = false,
        aiSearchEnabled: aiSearchEnabledSetting = false,
    } = useAppState<UserSettingsState>(s => s.userSettings);
    let availableStores = useAppState<ConnectedStore[]>(s => s.session.availableStores) ?? [];

    availableStores = getStoresAvailableForUser(availableStores, user?.filteredStores);

    const { pathname, search } = useLocation();
    const { page, sort, query, columns, perPage, filterMap, tab } = parseSearchUrl(
        search,
        sessionColumns
    );

    const isExactPhrase = (x: string): boolean => !!x && x.startsWith('"') && x.endsWith('"');
    const clearQuotes = (x: string): string => (isExactPhrase(x) ? x.replace(/"/g, '') : x);

    const isExactSearch = (x: string): boolean => {
        if (x.includes(':')) return false;
        if (isExactPhrase(x)) return true;
        return sessionExactSearch;
    };

    const initialExactSearch = isExactSearch(query);
    const currentQuery = clearQuotes(query);

    const [exactSearch, setExactSearch] = useState(initialExactSearch);
    const [isAiSearching, setIsAiSearching] = useState(false);
    const [aiSearchError, setAiSearchError] = useState('');
    const [hasToggledAi, setHasToggledAi] = useState(false);

    const setAiPrompt = (value: string) => dispatch(aiPromptChanged(value));

    const setAiMessage = (value: string) => dispatch(aiMessageChanged(value));

    const aiTextAreaRef = useRef<HTMLTextAreaElement>(null);
    const searchFieldRef = useRef<HTMLDivElement>(null);
    const aiSearchEnabled = aiSearchEnabledSetting;
    const showClassicSearch = !aiSearchEnabled || tab === OrderTabType.Invoices;
    const isAiMode = aiSearchEnabled && tab !== OrderTabType.Invoices;
    const showQueryChip = !showClassicSearch && !!query;

    const dispatchSearch = (q: string) => {
        const orderQueryString = createEncodedSearchQueryString(
            parseQuery(q, exactSearch && showClassicSearch),
            getOrderSearchFilters(filterMap),
            sort,
            perPage,
            columns,
            page,
            OrderSearchTabType.Orders
        );

        const accountQueryStringForDispatch = createEncodedSearchQueryString(
            parseQuery(q, exactSearch && showClassicSearch),
            getAccountSearchFilters(filterMap),
            sort,
            perPage,
            columns,
            page,
            OrderSearchTabType.Invoices
        );

        dispatch(searchActions.searchOrders(orderQueryString));
        dispatch(searchActions.searchAccounts(accountQueryStringForDispatch));
    };

    const makeSearch = (q: string) => {
        if (isSearching && searchCancelToken) searchCancelToken.cancel();

        if (isSearchingAccounts && accountSearchCancelToken) accountSearchCancelToken.cancel();

        setResetTableSelects(true);

        const currentPhrase = parseQuery(q, isExactPhrase(q));
        const queryString = createEncodedSearchQueryString(
            currentPhrase,
            getOrderSearchFilters(filterMap),
            sort,
            perPage,
            columns,
            '1',
            tab
        );

        navigate(
            `${pathname}?${
                search !== '' && search === queryString ? search.replace('?', '') : queryString
            }`
        );
    };

    const onSubmit = (data: Inputs) => {
        if (aiSearchEnabled && tab !== OrderTabType.Invoices) {
            handleAiSearch();
            return;
        }

        const { searchPhrase } = data;

        if (isExactPhrase(searchPhrase) && !exactSearch) {
            setExactSearch(true);
            setValue(SEARCH_PHRASE, searchPhrase.replace(/"/g, ''));
            makeSearch(generateQuoteSearch(getValues(SEARCH_PHRASE)));
            return;
        }

        if (searchPhrase === query.replace(/"/g, '')) {
            dispatchSearch(query);
            return;
        }

        if (!isExactPhrase(searchPhrase) && exactSearch) {
            makeSearch(generateQuoteSearch(getValues(SEARCH_PHRASE)));
            return;
        }

        makeSearch(searchPhrase);
    };

    const handleFilterChange = (termKey: string | undefined, termFilter: string[]) => {
        if (!termKey) return;
        filterMap.set(termKey, termFilter);
        makeSearch(getValues(SEARCH_PHRASE));
    };

    const onExactSearchChange = () => {
        const searchPhrase = getValues(SEARCH_PHRASE);

        setExactSearch(!exactSearch);
        dispatch(saveExactSearch(!exactSearch));

        if (isExactPhrase(query)) {
            makeSearch(searchPhrase);
            return;
        }

        if (exactSearch) {
            makeSearch(searchPhrase.replace(/"/g, ''));
            return;
        }

        makeSearch(generateQuoteSearch(searchPhrase));
    };

    const handleAiSearch = async (prompt?: string) => {
        const searchText = prompt ?? aiPrompt;
        if (!searchText?.trim()) return;

        setAiSearchError('');
        setAiMessage('');
        setIsAiSearching(true);
        try {
            const result = await interpretOrderSearch(searchText);

            setAiMessage(result.message ?? '');

            const backendParams = new URLSearchParams(result.searchQueryString);
            const queryFromResult = backendParams.get('query') ?? '';
            setValue(SEARCH_PHRASE, queryFromResult);
            setResetTableSelects(true);

            const uiParams = [
                'page=1',
                `perPage=${perPage}`,
                tab && `tab=${tab}`,
                !backendParams.has('sort') && sort && `sort=${sort}`,
                !backendParams.has('columns') &&
                    columns.length > 0 &&
                    `columns=${encodeURI(columns.join(','))}`,
            ]
                .filter(Boolean)
                .join('&');

            navigate(`${pathname}?${result.searchQueryString}&${uiParams}`);
        } catch {
            setAiSearchError('AI search failed. Falling back to regular search.');
            makeSearch(getValues(SEARCH_PHRASE));
        } finally {
            setIsAiSearching(false);
        }
    };

    const clearAllFilters = () => {
        filterMap.clear();
        setValue(SEARCH_PHRASE, '');
        setAiPrompt('');
        setAiMessage('');
        setAiSearchError('');
        makeSearch(getValues(SEARCH_PHRASE));
    };

    const removeQuery = () => {
        setValue(SEARCH_PHRASE, '');
        makeSearch('');
    };

    const hasNothingToClear = () =>
        filterMap.size === 0 && !getValues(SEARCH_PHRASE) && !aiPrompt.trim();

    const clearAiSearch = () => {
        filterMap.clear();
        setValue(SEARCH_PHRASE, '');
        setAiPrompt('');
        setAiSearchError('');
        setAiMessage('');
        setResetTableSelects(true);

        const queryString = createEncodedSearchQueryString(
            '',
            filterMap,
            null,
            perPage,
            columns,
            '1',
            tab
        );

        navigate(`${pathname}?${queryString}`);
    };

    const clearSearch = () => (isAiMode ? clearAiSearch() : clearAllFilters());

    if (clearSearchRef) clearSearchRef.current = clearSearch;

    const renderTerms = () => {
        return (
            <Flex row gap={theme.layout.gap.xsmall} flexWrap="wrap">
                {tab === OrderTabType.Orders &&
                    dateRanges.map(x => (
                        <DateRange
                            key={x.label}
                            maxDate={x.maxDate}
                            label={x.label}
                            onDateChange={handleFilterChange}
                            initialValue={parseFilters(x.SearchTerm, filterMap)}
                            dateRangeType={x.SearchTerm}
                            relativeRanges={x.ranges}
                            relativeLabel={x.relativeLabel}
                        />
                    ))}

                {!terms.some(x => x.key === 'storeId') && (
                    <StoreTerm
                        key={'storeId'}
                        currentFilters={parseFilters('storeId', filterMap)}
                        availableStores={availableStores}
                        onTermFilterChange={handleFilterChange}
                    />
                )}

                {tab === OrderTabType.Orders &&
                    terms.map(x => (
                        <Term
                            key={x.key}
                            category={TermCategory.Order}
                            term={x}
                            onTermFilterChange={handleFilterChange}
                            currentFilters={parseFilters(x.key, filterMap)}
                            showSearchbar={['storeId']}
                            availableStores={x.key === 'storeId' ? availableStores : []}
                            knownValues={x.key === 'status' ? ORDER_STATUSES : undefined}
                        />
                    ))}

                {tab === OrderTabType.Orders &&
                    [...AI_ONLY_FILTER_KEYS, ...RANGE_FILTER_KEYS]
                        .filter(key => (filterMap.get(key)?.length ?? 0) > 0)
                        .map(key => (
                            <ReadOnlyFilterTag
                                key={key}
                                filterKey={key}
                                values={filterMap.get(key)!}
                                onRemove={k => handleFilterChange(k, [])}
                            />
                        ))}

                {showQueryChip && (
                    <ReadOnlyFilterTag
                        filterKey="query"
                        values={[query]}
                        onRemove={() => removeQuery()}
                    />
                )}

                <ClearAllButton
                    title="Clear all search"
                    data-testid="search-clear-all-button"
                    onClick={() => clearAllFilters()}
                    disabled={hasNothingToClear()}
                >
                    <CloseLineIcon size={theme.icon.size.small} />
                </ClearAllButton>
            </Flex>
        );
    };

    const searchPhrase = register(SEARCH_PHRASE, searchValidationConfig);

    const searchWithAiOrRepeat = () =>
        aiPrompt.trim() ? handleAiSearch() : dispatchSearch(query);

    const copyTypedTextToEmptyAiPrompt = () => {
        const typedText = getValues(SEARCH_PHRASE);
        if (!typedText) return;
        if (aiPrompt.trim()) return;

        setAiPrompt(typedText);
    };

    const toggleAiSearch = (enabled: boolean) => {
        setHasToggledAi(true);
        if (enabled) copyTypedTextToEmptyAiPrompt();

        dispatch(setAiSearchEnabled(enabled));
    };

    const forgetAiFeedbackIfQueryChanged = () => {
        if (getValues(SEARCH_PHRASE) === currentQuery) return;

        setAiMessage('');
    };

    const onUnifiedSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        if (!isAiMode) {
            forgetAiFeedbackIfQueryChanged();
            return handleSubmit(onSubmit)(e);
        }

        e.preventDefault();
        searchWithAiOrRepeat();
    };

    const submitOnEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key !== 'Enter') return;
        if (isAiMode && e.shiftKey) return;

        e.preventDefault();
        e.currentTarget.form?.requestSubmit();
    };

    const renderStandardTextArea = () => (
        <SearchTextArea
            key="standard"
            ref={searchPhrase.ref}
            name={searchPhrase.name}
            onChange={searchPhrase.onChange}
            onBlur={searchPhrase.onBlur}
            defaultValue={currentQuery}
            id="order-search"
            aria-label="What are you looking for?"
            data-testid="search-input"
            rows={1}
            onKeyDown={submitOnEnter}
            onInput={e => fitToContent(e.currentTarget)}
            placeholder="What are you looking for?"
            $ai={false}
            $animate={hasToggledAi}
        />
    );

    const renderAiTextArea = () => (
        <SearchTextArea
            key="ai"
            ref={aiTextAreaRef}
            id="order-search"
            aria-label="Describe the orders you're looking for"
            data-testid="ai-prompt-textarea"
            rows={1}
            maxLength={2500}
            value={aiPrompt}
            onChange={e => setAiPrompt(e.target.value)}
            onKeyDown={submitOnEnter}
            disabled={isAiSearching}
            placeholder="Describe the orders you're looking for..."
            $ai
            $animate={hasToggledAi}
        />
    );

    const renderExactMatch = () => (
        <ExactMatch>
            <CheckBox
                dataTestId="exactMatch"
                checked={exactSearch}
                onChange={() => onExactSearchChange()}
            >
                Exact match
            </CheckBox>
        </ExactMatch>
    );

    const renderUnifiedSearch = () => (
        <form onSubmit={onUnifiedSubmit} data-testid="form">
            <SearchRow>
                <SearchField ref={searchFieldRef}>
                    <FocusLock disabled={!isSearching}>
                        {isAiMode ? renderAiTextArea() : renderStandardTextArea()}
                    </FocusLock>
                    {tab !== OrderTabType.Invoices && (
                        <FieldToggle>
                            <AiToggle
                                enabled={isAiMode}
                                onChange={toggleAiSearch}
                                disabled={isAiSearching}
                                attention={
                                    !hasToggledAi && !currentQuery && filterMap.size === 0
                                }
                            />
                        </FieldToggle>
                    )}
                </SearchField>

                {isAiMode ? (
                    <AiInterpretationSlot>
                        {showAiInterpretation && renderAiInterpretation()}
                    </AiInterpretationSlot>
                ) : (
                    renderExactMatch()
                )}

                <SearchButtons>
                    <SearchButton
                        type="submit"
                        primary
                        large
                        aria-label="Search"
                        disabled={isAiSearching}
                        data-testid={isAiMode ? 'ai-search-button' : 'order-search-button'}
                    >
                        {isAiSearching ? (
                            <Spinner color={theme.colors.button.primary.text} size={4} loading />
                        ) : (
                            <>
                                {isAiMode && <MagicLineIcon size={16} />}
                                Search
                            </>
                        )}
                    </SearchButton>

                    <ClearButton
                        type="button"
                        onClick={() => clearSearch()}
                        disabled={isAiSearching || hasNothingToClear()}
                        title="Clear all search"
                        data-testid={isAiMode ? 'ai-clear-button' : 'search-clear-button'}
                    >
                        Clear search and filters
                    </ClearButton>
                </SearchButtons>
            </SearchRow>
        </form>
    );

    const renderAiInterpretation = () => (
        <AiInterpretation data-testid="ai-interpretation">
            {aiSearchError && (
                <AiInterpretationError data-testid="ai-search-error">
                    {aiSearchError}
                </AiInterpretationError>
            )}
            {(isAiSearching || aiMessage) && (
                <InterpretationFeedback
                    message={aiMessage}
                    disclaimer={AI_DISCLAIMER}
                    isLoading={isAiSearching}
                    onExampleClick={example => {
                        setAiPrompt(example);
                        handleAiSearch(example);
                    }}
                />
            )}
        </AiInterpretation>
    );

    const showAiInterpretation = isAiMode && (isAiSearching || !!aiMessage || !!aiSearchError);

    useLayoutEffect(
        () => fitToContent(searchFieldRef.current?.querySelector('textarea')),
        [isAiMode, aiPrompt, query]
    );

    useEffect(() => {
        dispatch(searchActions.terms());
        dispatch(setPerPage(perPage));
    }, [perPage]);

    useEffect(() => {
        dispatchSearch(query);
    }, [search]);

    return (
        <Flex gap={theme.layout.gap.xsmall} column>
            {renderUnifiedSearch()}

            {terms && terms.length > 0 && renderTerms()}

            {errors.searchPhrase && (
                <p className="error-message" role="alert">
                    {errors.searchPhrase?.message}
                </p>
            )}
        </Flex>
    );
};

const ClearAllButton = styled.button`
    display: flex;
    align-items: center;
    background: ${props => props.theme.colors.button.light};
    color: ${props => props.theme.colors.black};
    border-radius: 0.5rem;
    border: none;

    &:disabled {
        opacity: 0.5;
    }

    &:hover:enabled {
        background: ${props => props.theme.colors.black};
        color: ${props => props.theme.colors.light};
        transition: all 0.6s;
        cursor: pointer;
    }
`;

const FIELD_WIDTH = '53rem';
const SIDE_COLUMN_WIDTH = '60rem';

const SearchRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, ${FIELD_WIDTH}) minmax(0, ${SIDE_COLUMN_WIDTH});
    grid-template-rows: minmax(4rem, auto) 4rem;
    gap: ${props => props.theme.layout.gap.small};
    margin-bottom: ${props => props.theme.layout.gap.small};
`;

const SearchField = styled.div`
    position: relative;
    grid-column: 1;
    grid-row: 1;
    min-width: 0;
`;

const SearchButtons = styled.div`
    display: flex;
    grid-column: 1;
    grid-row: 2;
    gap: ${props => props.theme.layout.gap.small};
`;

// No z-index: a stacking context here would stop the AI sheen from blending with the field.
const FieldToggle = styled.div`
    position: absolute;
    top: 0.6rem;
    right: 0.5rem;
`;

const fieldRipple = keyframes`
    from {
        box-shadow: 0 0 0 0 var(--ripple-color);
    }
    to {
        box-shadow: 0 0 0 0.8rem transparent;
    }
`;

const SearchTextArea = styled.textarea<{ $ai: boolean; $animate: boolean }>`
    display: block;
    width: 100%;
    height: 4rem;
    padding: 0.9rem 7rem 0.9rem 1rem;
    box-sizing: border-box;
    border: 0.1rem solid
        ${props => (props.$ai ? props.theme.colors.primary : props.theme.colors.border)};
    border-radius: 0.3rem;
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text.primary};
    font-family: inherit;
    font-size: inherit;
    line-height: 2rem;
    overflow-y: hidden;
    resize: none;
    outline: none;
    --ripple-color: ${props =>
        props.$ai ? 'rgba(232, 101, 88, 0.6)' : props.theme.colors.mediumPrimary};
    ${props =>
        props.$animate &&
        css`
            animation: ${fieldRipple} 500ms ease-out;
        `}

    &::placeholder {
        color: ${props => props.theme.colors.text.secondary};
        opacity: 1;
    }

    &:focus {
        border: 0.1rem solid ${props => props.theme.colors.primary};
    }

    &:disabled {
        opacity: 0.5;
    }
`;

const SearchButton = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
`;

const ClearButton = styled(Button)`
    height: 4rem;
    padding: 0 ${props => props.theme.layout.padding.medium};

    &:disabled {
        cursor: not-allowed;
    }
`;

const ExactMatch = styled.div`
    display: flex;
    grid-column: 2;
    grid-row: 1;
    align-self: start;
    align-items: center;
    height: 4rem;
`;

const AiInterpretationSlot = styled.div`
    position: relative;
    grid-column: 2;
    grid-row: 1 / span 2;
    min-width: 0;
    min-height: 0;
`;

// Absolute so an expanded message overlays the filters instead of scrolling or shifting them.
const AiInterpretation = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-height: 100%;
    box-sizing: border-box;
    padding: 0.6rem 1rem;
    border-radius: 0.3rem;
    background: linear-gradient(
            ${props => props.theme.colors.lightPrimary},
            ${props => props.theme.colors.lightPrimary}
        ),
        ${props => props.theme.colors.subtle.light};
    line-height: 1.5rem;
`;

const AiInterpretationError = styled.p`
    margin: 0;
    color: ${props => props.theme.colors.error};
    font-size: ${props => props.theme.text.size.small};
`;

const InterpretationFeedback = styled(AiFeedback)`
    width: 100%;

    [data-testid='ai-feedback-disclaimer'] {
        font-size: ${props => props.theme.text.size.xsmall};
    }
`;

export default withTheme(SearchForm) as React.ComponentType<Omit<SearchFormProps, 'theme'>>;
